import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Globe,
  Mail,
  Plus,
  Printer,
  Save,
  Trash2,
  X,
} from 'lucide-react';
import { api } from '../api/client';
import { SkeletonCard, SkeletonTable } from '../components/Skeleton';
import { toast } from '../lib/toast';
import { formatDate } from '../lib/format';

const STATUS_META = {
  draft: { label: 'Draft', class: 'bg-gray-100 text-gray-700' },
  sent: { label: 'Sent', class: 'bg-brand-50 text-brand-700' },
  paid: { label: 'Paid', class: 'bg-emerald-50 text-emerald-700' },
  overdue: { label: 'Overdue', class: 'bg-rose-50 text-rose-700' },
  void: { label: 'Void', class: 'bg-gray-100 text-gray-500' },
};

const CURRENCY_SYMBOL = { USD: '$', EUR: '€', GBP: '£', INR: '₹' };
const currencySym = (c) => CURRENCY_SYMBOL[c] || c || '$';

export default function Invoice() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const invoiceQuery = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => api.get(`/api/invoices/${id}`).then((r) => r.data.invoice),
  });

  const updateMutation = useMutation({
    mutationFn: (body) =>
      api.put(`/api/invoices/${id}`, body).then((r) => r.data.invoice),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice', id] });
      toast.success('Invoice saved');
    },
    onError: (err) => toast.error(err, 'Could not save invoice'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/api/invoices/${id}`),
    onSuccess: () => {
      toast.success('Invoice deleted');
      navigate('/');
    },
    onError: (err) => toast.error(err, 'Could not delete invoice'),
  });

  const invoice = invoiceQuery.data;
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (invoice && !form) {
      setForm({
        issue_date: invoice.issue_date
          ? new Date(invoice.issue_date).toISOString().slice(0, 10)
          : '',
        due_date: invoice.due_date
          ? new Date(invoice.due_date).toISOString().slice(0, 10)
          : '',
        currency: invoice.currency || 'USD',
        tax_rate: invoice.tax_rate || 0,
        notes: invoice.notes || '',
        line_items:
          invoice.line_items?.length > 0
            ? invoice.line_items.map((li) => ({ ...li }))
            : [{ description: '', qty: 1, unit_price: 0 }],
        status: invoice.status,
      });
    }
  }, [invoice, form]);

  const totals = useMemo(() => {
    if (!form) return { subtotal: 0, tax: 0, total: 0 };
    const subtotal = form.line_items.reduce(
      (s, li) => s + (Number(li.qty) || 0) * (Number(li.unit_price) || 0),
      0,
    );
    const tax = +(subtotal * ((Number(form.tax_rate) || 0) / 100)).toFixed(2);
    const total = +(subtotal + tax).toFixed(2);
    return { subtotal: +subtotal.toFixed(2), tax, total };
  }, [form]);

  if (invoiceQuery.isLoading || !form) {
    return (
      <div className="min-h-screen p-6">
        <div className="max-w-4xl mx-auto space-y-4">
          <SkeletonCard lines={3} />
          <SkeletonTable rows={5} cols={4} />
          <SkeletonCard lines={2} />
        </div>
      </div>
    );
  }
  if (invoiceQuery.error || !invoice) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="card max-w-md text-center">
          <div className="mx-auto mb-4 h-14 w-14 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center">
            <AlertTriangle className="w-7 h-7" />
          </div>
          <h2 className="text-lg font-semibold">Invoice not found</h2>
          <Link to="/" className="btn-primary inline-flex mt-4">
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </Link>
        </div>
      </div>
    );
  }

  const branding = invoice.branding_snapshot || {};
  const client = invoice.client_id || {};
  const primary = branding.primaryColor || '#d61f97';
  const accent = branding.accentColor || '#ffa23d';
  const styleVars = { '--brand': primary, '--brand-accent': accent };

  const onSave = () => {
    updateMutation.mutate({
      issue_date: form.issue_date || null,
      due_date: form.due_date || null,
      currency: form.currency,
      tax_rate: Number(form.tax_rate) || 0,
      notes: form.notes,
      line_items: form.line_items
        .filter((li) => li.description?.trim())
        .map((li) => ({
          description: li.description,
          qty: Number(li.qty) || 0,
          unit_price: Number(li.unit_price) || 0,
        })),
    });
  };

  const onChangeStatus = (status) => {
    setForm({ ...form, status });
    updateMutation.mutate({ status });
  };

  const addLineItem = () =>
    setForm({
      ...form,
      line_items: [...form.line_items, { description: '', qty: 1, unit_price: 0 }],
    });
  const updateLineItem = (idx, patch) =>
    setForm({
      ...form,
      line_items: form.line_items.map((li, i) => (i === idx ? { ...li, ...patch } : li)),
    });
  const removeLineItem = (idx) =>
    setForm({
      ...form,
      line_items: form.line_items.filter((_, i) => i !== idx),
    });

  return (
    <div className="min-h-screen bg-white" style={styleVars}>
      {/* Toolbar — hidden in print */}
      <div className="border-b border-gray-100 bg-white/80 backdrop-blur sticky top-0 z-10 print-hide">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between gap-3 flex-wrap">
          <Link to="/" className="btn-ghost py-1.5">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={form.status}
              onChange={(e) => onChangeStatus(e.target.value)}
              className={`text-xs rounded-lg px-2 py-1.5 border bg-white ${STATUS_META[form.status]?.class} border-current/30`}
            >
              {Object.entries(STATUS_META).map(([id, m]) => (
                <option key={id} value={id}>
                  {m.label}
                </option>
              ))}
            </select>
            <button className="btn-secondary" onClick={onSave} disabled={updateMutation.isPending}>
              <Save className="w-4 h-4" />
              {updateMutation.isPending ? 'Saving…' : 'Save'}
            </button>
            <button className="btn-primary" onClick={() => window.print()}>
              <Printer className="w-4 h-4" /> Save as PDF
            </button>
            <button
              className="btn-icon hover:!text-rose-600 hover:!bg-rose-50"
              title="Delete invoice"
              onClick={() => {
                if (window.confirm(`Delete invoice ${invoice.number}?`)) {
                  deleteMutation.mutate();
                }
              }}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="print-scorecard max-w-4xl mx-auto px-4 sm:px-8 py-8">
        {/* Header */}
        <header
          className="relative rounded-2xl overflow-hidden p-8 mb-8 text-white"
          style={{
            background:
              'linear-gradient(135deg, var(--brand) 0%, var(--brand-accent) 100%)',
          }}
        >
          <div className="absolute inset-0 bg-grid opacity-15" />
          <div className="relative flex items-start justify-between gap-4 flex-wrap">
            <div>
              {branding.logoUrl && (
                <img
                  src={branding.logoUrl}
                  alt={branding.agencyName || ''}
                  className="h-10 w-auto mb-4 bg-white/95 rounded-lg p-1.5"
                  onError={(e) => (e.target.style.display = 'none')}
                />
              )}
              <div className="text-xs uppercase tracking-wider opacity-80 mb-1">
                {branding.agencyName || 'Invoice'}
              </div>
              <h1 className="text-3xl font-bold leading-tight">
                Invoice {invoice.number}
              </h1>
              {branding.tagline && (
                <p className="mt-2 text-white/85">{branding.tagline}</p>
              )}
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wider opacity-80">Total</div>
              <div className="text-3xl font-bold tabular-nums">
                {currencySym(form.currency)}
                {formatMoney(totals.total)}
              </div>
              <div className="mt-2 inline-block">
                <span className={`pill bg-white/90 text-ink-900`}>
                  {STATUS_META[form.status]?.label}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Parties + dates */}
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
          <div className="rounded-2xl border border-gray-100 p-5">
            <div className="text-xs uppercase tracking-wider text-ink-400 font-semibold mb-2">
              From
            </div>
            <div className="font-semibold text-ink-900">
              {branding.agencyName || '(set your agency name in Reports → Brand profile)'}
            </div>
            <div className="text-sm text-ink-700 mt-1 space-y-0.5">
              {branding.contactEmail && (
                <div className="flex items-center gap-1.5">
                  <Mail className="w-3 h-3" /> {branding.contactEmail}
                </div>
              )}
              {branding.contactUrl && (
                <div className="flex items-center gap-1.5">
                  <Globe className="w-3 h-3" />{' '}
                  {branding.contactUrl.replace(/^https?:\/\//, '')}
                </div>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-gray-100 p-5">
            <div className="text-xs uppercase tracking-wider text-ink-400 font-semibold mb-2">
              Bill to
            </div>
            <div className="font-semibold text-ink-900">
              {client.company_name || client.name || 'Client'}
            </div>
            {client.company_name && client.name && (
              <div className="text-sm text-ink-700">{client.name}</div>
            )}
            <div className="text-sm text-ink-700 mt-1 space-y-0.5">
              {client.email && (
                <div className="flex items-center gap-1.5">
                  <Mail className="w-3 h-3" /> {client.email}
                </div>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-gray-100 p-5 sm:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <DateField
              label="Issue date"
              value={form.issue_date}
              onChange={(v) => setForm({ ...form, issue_date: v })}
            />
            <DateField
              label="Due date"
              value={form.due_date}
              onChange={(v) => setForm({ ...form, due_date: v })}
            />
            <div>
              <div className="text-xs uppercase tracking-wide text-ink-400 font-semibold mb-1">
                Currency
              </div>
              <select
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
                className="input print-hide"
              >
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="GBP">GBP (£)</option>
                <option value="INR">INR (₹)</option>
              </select>
              <div className="hidden print:block text-sm font-semibold text-ink-900">
                {form.currency}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-ink-400 font-semibold mb-1">
                Tax rate (%)
              </div>
              <input
                type="number"
                min="0"
                max="100"
                step="any"
                value={form.tax_rate}
                onChange={(e) => setForm({ ...form, tax_rate: e.target.value })}
                className="input print-hide"
              />
              <div className="hidden print:block text-sm font-semibold text-ink-900">
                {form.tax_rate}%
              </div>
            </div>
          </div>
        </section>

        {/* Line items */}
        <section className="mb-8">
          <div className="text-xs uppercase tracking-wider text-ink-400 font-semibold mb-2">
            Items
          </div>
          <div className="rounded-2xl border border-gray-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs uppercase tracking-wide text-ink-400">
                  <th className="px-4 py-2.5">Description</th>
                  <th className="px-4 py-2.5 text-right w-20">Qty</th>
                  <th className="px-4 py-2.5 text-right w-32">Unit price</th>
                  <th className="px-4 py-2.5 text-right w-32">Amount</th>
                  <th className="px-2 py-2.5 w-10 print-hide" />
                </tr>
              </thead>
              <tbody>
                {form.line_items.map((li, idx) => {
                  const amount = (Number(li.qty) || 0) * (Number(li.unit_price) || 0);
                  return (
                    <tr key={idx} className="border-t border-gray-50">
                      <td className="px-4 py-2">
                        <input
                          className="input print-hide"
                          value={li.description}
                          onChange={(e) =>
                            updateLineItem(idx, { description: e.target.value })
                          }
                          placeholder="Service description"
                        />
                        <div className="hidden print:block text-ink-800">
                          {li.description || '—'}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <input
                          className="input text-right tabular-nums print-hide"
                          type="number"
                          min="0"
                          step="any"
                          value={li.qty}
                          onChange={(e) =>
                            updateLineItem(idx, { qty: e.target.value })
                          }
                        />
                        <div className="hidden print:block tabular-nums">
                          {li.qty}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <input
                          className="input text-right tabular-nums print-hide"
                          type="number"
                          min="0"
                          step="any"
                          value={li.unit_price}
                          onChange={(e) =>
                            updateLineItem(idx, { unit_price: e.target.value })
                          }
                        />
                        <div className="hidden print:block tabular-nums">
                          {currencySym(form.currency)}
                          {formatMoney(li.unit_price)}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums font-medium text-ink-900">
                        {currencySym(form.currency)}
                        {formatMoney(amount)}
                      </td>
                      <td className="px-2 py-2 text-right print-hide">
                        <button
                          type="button"
                          className="btn-icon h-8 w-8 hover:!text-rose-600 hover:!bg-rose-50"
                          onClick={() => removeLineItem(idx)}
                          title="Remove"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="border-t border-gray-100 p-3 print-hide">
              <button type="button" className="btn-secondary" onClick={addLineItem}>
                <Plus className="w-4 h-4" /> Add line item
              </button>
            </div>
          </div>
        </section>

        {/* Totals */}
        <section className="mb-8 flex justify-end">
          <div className="w-full sm:w-72 rounded-2xl border border-gray-100 p-5 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-ink-400">Subtotal</span>
              <span className="tabular-nums">
                {currencySym(form.currency)}
                {formatMoney(totals.subtotal)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-ink-400">Tax ({form.tax_rate || 0}%)</span>
              <span className="tabular-nums">
                {currencySym(form.currency)}
                {formatMoney(totals.tax)}
              </span>
            </div>
            <div className="border-t border-gray-100 pt-2 flex justify-between font-semibold text-base">
              <span>Total</span>
              <span className="tabular-nums">
                {currencySym(form.currency)}
                {formatMoney(totals.total)}
              </span>
            </div>
          </div>
        </section>

        {/* Notes */}
        <section className="mb-8">
          <div className="text-xs uppercase tracking-wider text-ink-400 font-semibold mb-2">
            Notes
          </div>
          <textarea
            className="input min-h-[80px] print-hide"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Payment terms, bank details, thank-you note…"
          />
          <div className="hidden print:block text-sm text-ink-800 whitespace-pre-wrap">
            {form.notes || ''}
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-gray-100 pt-5 text-xs text-ink-400 flex items-center justify-between gap-4 flex-wrap">
          <div>{branding.footerNote || ''}</div>
          <div>
            Issued{' '}
            {form.issue_date ? formatDate(form.issue_date) : formatDate(invoice.createdAt)}
          </div>
        </footer>

        {form.status === 'paid' && invoice.paid_at && (
          <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-700 text-sm p-3 inline-flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Paid on {formatDate(invoice.paid_at)}
          </div>
        )}
      </div>
    </div>
  );
}

function DateField({ label, value, onChange }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-ink-400 font-semibold mb-1">
        {label}
      </div>
      <input
        type="date"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="input print-hide"
      />
      <div className="hidden print:block text-sm font-semibold text-ink-900">
        {value ? formatDate(value) : '—'}
      </div>
    </div>
  );
}

function formatMoney(n) {
  const num = Number(n) || 0;
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
